// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { CustomErrorClass, ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import ModifyOrderScreenTemplate from '@front/templates/ModifyOrderScreenTemplate.html';
import ModifyOrderFooterTemplate from '@front/templates/ModifyOrderFooterTemplate.html';
import OrderEditControlsTemplate from '@front/templates/OrderEditControlsTemplate.html';
import { Control } from '../elements/Control';
import { ScreensNames } from '../UtilsClasses/FactoryConstants';
import { OrderEditViewBase } from '../panels/OrderEditViewBase';
import { TIF } from '@shared/utils/Trading/OrderTif';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { modifyOrderScreenHandler } from '@shared/utils/AppHandlers';
import { OrderEditUpdateData } from '@shared/utils/Trading/OrderEditUpdateData';
import { DataCache } from '@shared/commons/DataCache';
import { TradingNumericErrorChecker } from '@shared/commons/Trading/TradingNumericErrorChecker';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { ResourcesManager } from '@shared/commons/properties/ResourcesManager';
import { IsAllowed } from '@shared/commons/IsAllowed';
// TODO. Refactor. Duplicate code of ModifyPositionScreen.
export class ModifyOrderScreen extends OrderEditViewBase {
    getType() {
        return ScreensNames.ModifyOrderScreen;
    }
    oninit() {
        super.oninit();
        this.on('modifyBtnClick', this.modify);
        this.on('cancelBtnClick', this.close);
        this.on('onBreakevenClick', this.setBreakevenPrice);
        this.observe('order', this.onOrder);
        this.observe('tradingAllowed', this.onTradingAllowed);
        this.observe('quantity tif disclosedQuantity userComment', this.onTradingDataChanged);
        // TODO. Refactor.
        // TickAsync/Localize doesn't work for panels added with addScreen method.
        ResourcesManager.onLocaleChange.Subscribe(this.localize, this);
        Control.Ticker.Subscribe(this.TickAsync, this);
    }
    // TODO. Refactor.
    oncomplete() {
        super.oncomplete();
        this.onTradingAllowed(this.get('tradingAllowed'));
        void this.set('QNumericLink', this.Controls.quantity);
        this.localize();
        this.addScrollToContainer('.modify-order-screen-container');
        this.center();
    }
    dispose() {
        ResourcesManager.onLocaleChange.UnSubscribe(this.localize, this);
        Control.Ticker.UnSubscribe(this.TickAsync, this);
        super.dispose();
    }
    localize() {
        OrderEditViewBase.prototype.localize.call(this);
        this.updatePanelHeader();
        void this.set({
            disclosedLabel: Resources.getResource('panel.newOrderEntry.disclosedLabel'),
            modifyBtnText: Resources.getResource('screen.modifyOrder.modify'),
            cancelBtnText: Resources.getResource('general.messageBox.cancel')
        });
    }
    // TODO. UGLY.
    onOrder(order) {
        if (!order) {
            return;
        }
        const orderTypeDict = DataCache.OrderParameterContainer.OrderTypes;
        const orderTypeObj = orderTypeDict[order.OrderType];
        this.setOrderEdit(orderTypeObj.createModifyOrderObject({
            dataCache: DataCache,
            order
        }));
        void this.set({ account: order.Account, instrument: order.Instrument }).then(function () {
            // TEST.
            void this.set({
                side: order.BuySell,
                orderType: orderTypeObj,
                productType: order.ProductType,
                userComment: order.UserComment,
                enableComment: order.Instrument.IsCommentEnabled(orderTypeObj),
                // TODO. Remove. Ugly.
                defaultTif: new TIF(order.TimeInForce, new Date(order.ExpireAt)),
                // TODO. Remove. Ugly.
                defaultQuantity: new Quantity(order.Amount, true)
            });
        }.bind(this));
        this.updatePanelHeader();
    }
    onTradingDataChanged(newVal, oldVal, key) {
        const orderEdit = this.orderEdit;
        if (!orderEdit)
            return;
        const tradingDataDict = {};
        tradingDataDict[key] = newVal;
        this.addDisclosedQuantityIfNeed(key);
        orderEdit.updateParameters(new OrderEditUpdateData(null, tradingDataDict));
    }
    onTradingAllowed(allowed) {
        void this.set('enabledBtn', allowed);
    }
    setBreakevenPrice(context, price) {
        const orderEdit = this.orderEdit;
        if (orderEdit) {
            orderEdit.setBreakevenPrice(price);
        }
    }
    updatePanelHeader() {
        const ord = this.get('order');
        if (!ord)
            return;
        void this.set('header', Resources.getResource('screen.modifyOrder.Change order') + ' (' + ord.OrderNumber + ')');
    }
    layout() {
        this.updateOrderEditParametersVisibility();
    }
    modify() {
        if (TradingNumericErrorChecker.HasErrors(this)) {
            return;
        }
        this.orderEdit.placedFrom = this.get('placedFrom');
        DataCache.FOrderExecutor.modifyOrderPromiseWithCallbacks(this.orderEdit, this.close.bind(this), this.focusWarningNumeric.bind(this))
            .then(() => { })
            .catch(() => {
            const ex = new CustomErrorClass('ModifyOrderScreen error', 'ModifyOrderScreen.modify', 'modify -> modifyOrderPromiseWithCallbacks');
            ErrorInformationStorage.GetException(ex);
        });
    }
    static getScreenByOrderID(orderID) {
        var _a;
        const keys = Object.keys(MainWindowManager.MainWindow.Controls);
        const ModifyOrderScreenType = ScreensNames.ModifyOrderScreen;
        for (let i = keys.length - 1; i >= 0; i--) {
            const control = MainWindowManager.MainWindow.Controls[keys[i]];
            if (((_a = control === null || control === void 0 ? void 0 : control.getType) === null || _a === void 0 ? void 0 : _a.call(control)) === ModifyOrderScreenType) {
                const order = control.get('order');
                if (order && order.OrderNumber === orderID) {
                    return control;
                }
            }
        }
        return null;
    }
    static show(orderId, placedFrom, numericLinkName) {
        const alreadyOpenedScreen = ModifyOrderScreen.getScreenByOrderID(orderId);
        let oldLocation = null;
        let notSkipNextTime = false;
        if (alreadyOpenedScreen) {
            oldLocation = { x: alreadyOpenedScreen.get('left'), y: alreadyOpenedScreen.get('top') };
            notSkipNextTime = alreadyOpenedScreen.NotSkipNextTime;
            alreadyOpenedScreen.close();
        }
        const screen = MainWindowManager.Factory.addScreen(ScreensNames.ModifyOrderScreen);
        screen.set({
            order: DataCache.Trading.getOrderById(orderId),
            placedFrom
        });
        if (numericLinkName) {
            screen.activateNumeric(numericLinkName);
        }
        if (oldLocation) {
            screen.setLocation(oldLocation.x, oldLocation.y);
        }
        screen.NotSkipNextTime = notSkipNextTime; // TODO костыль ебаный
        screen.set('disableProductType', true);
    }
    static modifyOrderScreenHandlerInitialize() {
        modifyOrderScreenHandler.show = ModifyOrderScreen.show;
    }
}
OrderEditViewBase.extendWith(ModifyOrderScreen, {
    data: function () {
        return {
            resizable: false,
            width: 400,
            dockablePanel: false,
            attached: false,
            showHeader: true,
            showFooter: true,
            header: '',
            order: null,
            tif: null,
            // TODO. Remove. Ugly.
            defaultTif: null,
            quantity: null,
            // TODO. Remove. Ugly.
            defaultQuantity: null,
            instrument: null,
            account: null,
            side: null,
            orderType: null,
            userComment: null,
            disclosedQuantityShow: false,
            disclosedQuantity: null,
            dqMinValue: null,
            dqMaxValue: null,
            dqTooltip: 'panel.newOrderEntry.disclosedLabel.tt',
            dqLessMinLocalKey: 'UserControl.TerceraDQNumeric.ValueLessMin',
            dqGreaterMaxLocalKey: 'UserControl.TerceraDQNumeric.ValueGreaterMax',
            style_addition_body: 'modify-order-screen-container',
            style_addition_footer: 'modify-order-screen-footer',
            productType: null,
            disableProductType: null,
            SLNumericLink: null,
            TPNumericLink: null,
            PriceNumericLink: null,
            StopPriceNumericLink: null,
            QNumericLink: null
        };
    },
    computed: {
        isRouteAllowModifyTIF() {
            return !!IsAllowed.RouteAllowModifyTIF(this.get('instrument'));
        }
    },
    partials: {
        // TODO. Refactor.
        bodyPartial: ModifyOrderScreenTemplate,
        footerPartial: ModifyOrderFooterTemplate,
        orderEditControls: OrderEditControlsTemplate
    }
});
